import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./ChallengeList.module.css";
import { useNavigate } from "react-router-dom";
import { open_api_with_cookies, getCountryCode } from "../../Utils/network";
import SearchBar from "../../Components/Card/SearchBar";
import GeneralChips from "../../Components/Chips/GeneralChips";
import SliderBase from "../../Components/Slider/SliderBase";
import ChallengeCard from "../../Components/ChallengeCard/ChallengeCard";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Spinning loader component for infinite scroll
const LoadingSpinner = () => (
  <div
    className={styles.spinner_container}
    aria-label="Loading more challenges"
  >
    <div className={styles.spinner}></div>
  </div>
);

// Skeleton for challenge cards
const ChallengeCardSkeleton = () => (
  <div
    className={styles.challenge_skeleton}
    aria-busy="true"
    aria-hidden="true"
  >
    <Skeleton height={200} width="100%" style={{ marginBottom: "10px" }} />
    <Skeleton height={24} width="80%" style={{ marginBottom: "8px" }} />
    <Skeleton height={18} width="60%" style={{ marginBottom: "8px" }} />
    <Skeleton height={16} width="40%" />
  </div>
);

const ChallengeList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChips, setSelectedChips] = useState(["all"]);
  const [chips, setChips] = useState([
    { id: 0, name: "all", display_name: "All" },
    { id: 1, name: "selfcare", display_name: "Self Care" },
    { id: 2, name: "fitness", display_name: "Fitness" },
    { id: 3, name: "nutrition", display_name: "Nutrition" },
    { id: 4, name: "wellness", display_name: "Wellness" },
  ]);
  const [challenges, setChallenges] = useState([]);
  const [filteredChallenges, setFilteredChallenges] = useState([]);
  const [visibleChallenges, setVisibleChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [maxPages, setMaxPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [countryCode, setCountryCode] = useState("in");
  const observer = useRef();
  const ITEMS_PER_PAGE = 12;

  // Intersection observer for infinite scroll
  const lastChallengeElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreChallenges();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, hasMore]
  );

  const loadMoreChallenges = useCallback(async () => {
    if (!hasMore || isLoadingMore || page >= maxPages) return;

    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;

      // Build query params for the API call
      const queryParams = new URLSearchParams();
      queryParams.append("page", nextPage);

      if (searchQuery) {
        queryParams.append("search", searchQuery);
      }

      // Add category filter if not "all"
      if (!selectedChips.includes("all") && selectedChips.length > 0) {
        queryParams.append("category", selectedChips[0]);
      }

      const result = await open_api_with_cookies.get(
        `/challenge/list/?${queryParams}`
      );

      const newChallenges = result?.data?.data || [];
      const paginationInfo = {
        nextPage: result?.data?.next_page,
        maxPages: result?.data?.max_pages,
        totalCount: result?.data?.total_count,
      };

      // Append new challenges to existing ones
      setVisibleChallenges((prev) => [...prev, ...newChallenges]);
      setPage(nextPage);
      setHasMore(nextPage < paginationInfo.maxPages);

      console.log(
        `Loaded page ${nextPage}/${paginationInfo.maxPages}. Total challenges: ${visibleChallenges.length + newChallenges.length}/${paginationInfo.totalCount}`
      );
    } catch (err) {
      console.error("Error loading more challenges:", err);
      setError("Failed to load more challenges.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    page,
    maxPages,
    selectedChips,
    searchQuery,
    visibleChallenges.length,
  ]);

  // Fetch initial challenges
  const getChallenges = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await open_api_with_cookies.get(`/challenge/list/?page=1`);
      const challengesData = result?.data?.data || [];
      const paginationInfo = {
        maxPages: result?.data?.max_pages || 1,
        totalCount: result?.data?.total_count || 0,
        nextPage: result?.data?.next_page,
      };

      setChallenges(challengesData);
      setFilteredChallenges(challengesData);
      setVisibleChallenges(challengesData);
      setHasMore(paginationInfo.nextPage ? true : false);
      setPage(1);
      setMaxPages(paginationInfo.maxPages);
      setTotalCount(paginationInfo.totalCount);
    } catch (err) {
      setError("Failed to load challenges. Please try again later.");
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter challenges based on search and categories
  const filterChallenges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (selectedChips.includes("all") && !searchQuery) {
        // Reset to first page when showing all challenges
        const result = await open_api_with_cookies.get(
          `/challenge/list/?page=1`
        );
        const challengesData = result?.data?.data || [];
        const paginationInfo = {
          maxPages: result?.data?.max_pages || 1,
          totalCount: result?.data?.total_count || 0,
          nextPage: result?.data?.next_page,
        };

        setFilteredChallenges(challengesData);
        setVisibleChallenges(challengesData);
        setHasMore(paginationInfo.nextPage ? true : false);
        setPage(1);
        setMaxPages(paginationInfo.maxPages);
        setTotalCount(paginationInfo.totalCount);
        setIsLoading(false);
        return;
      }

      const queryParams = new URLSearchParams();
      queryParams.append("page", 1); // Always start from page 1 when filtering

      if (searchQuery) {
        queryParams.append("search", searchQuery);
      }

      // Add category filter if not "all"
      if (!selectedChips.includes("all") && selectedChips.length > 0) {
        queryParams.append("category", selectedChips[0]);
      }

      const result = await open_api_with_cookies.get(
        `/challenge/list/?${queryParams}`
      );
      const challengesData = result?.data?.data || [];
      const paginationInfo = {
        maxPages: result?.data?.max_pages || 1,
        totalCount: result?.data?.total_count || 0,
        nextPage: result?.data?.next_page,
      };

      setFilteredChallenges(challengesData);
      setVisibleChallenges(challengesData);
      setHasMore(paginationInfo.nextPage ? true : false);
      setPage(1);
      setMaxPages(paginationInfo.maxPages);
      setTotalCount(paginationInfo.totalCount);
    } catch (err) {
      setError("Failed to filter challenges. Please try again.");
      console.error("Error filtering challenges:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Handle chip selection
  const handleClick = (event, chipName) => {
    setSelectedChips((prev) => {
      if (chipName === "all") {
        return ["all"];
      }

      if (prev.includes(chipName)) {
        const newChips = prev.filter((chip) => chip !== chipName);
        return newChips.length ? newChips : ["all"];
      }

      return [chipName]; // Only allow one category at a time for simplicity
    });
  };

  // Initial load
  useEffect(() => {
    const fetchCountry = async () => {
      const countryCode = await getCountryCode();
      setCountryCode(countryCode);
    };
    fetchCountry();
    getChallenges();
  }, []);

  // Filter when search or chips change
  useEffect(() => {
    filterChallenges();
  }, [searchQuery, selectedChips]);

  return (
    <>
      <div className={styles.container}>
        <div>
          <SliderBase page={"challenge"} />
        </div>
        <div className={styles.headers}>
          <h1 className={styles.title}>Discover amazing challenges</h1>
        </div>
        <SearchBar onSearch={handleSearch} text="Search for any challenges" />

        <div
          className={styles.chip_container}
          role="group"
          aria-label="Filter challenges by category"
        >
          {chips.map((chip) => (
            <GeneralChips
              title={chip.display_name}
              key={chip.id}
              handleClick={(event) => handleClick(event, chip.name)}
              selectedChips={
                selectedChips.includes(chip.name) ? [chip.display_name] : []
              }
              aria-pressed={selectedChips.includes(chip.name)}
            />
          ))}
        </div>

        {error && (
          <p className={styles.error_message} role="alert">
            {error}
          </p>
        )}

        <div className={styles.challenges_container}>
          {isLoading ? (
            <div className={styles.challenges_grid} aria-live="polite">
              {Array(12)
                .fill(0)
                .map((_, index) => (
                  <div key={index}>
                    <ChallengeCardSkeleton />
                  </div>
                ))}
            </div>
          ) : visibleChallenges.length > 0 ? (
            <>
              <div className={styles.challenges_grid}>
                {visibleChallenges.map((challenge, index) => (
                  <div
                    key={challenge.id || index}
                    ref={
                      index === visibleChallenges.length - 1
                        ? lastChallengeElementRef
                        : null
                    }
                  >
                    <ChallengeCard data={challenge} countryCode={countryCode} />
                  </div>
                ))}
              </div>
              {isLoadingMore && <LoadingSpinner />}
              {!isLoadingMore &&
                hasMore &&
                totalCount > visibleChallenges.length && (
                  <div className={styles.load_more_info}>
                    <p>
                      Showing {visibleChallenges.length} of {totalCount}{" "}
                      challenges
                    </p>
                  </div>
                )}
            </>
          ) : (
            <div className={styles.no_results} role="status">
              <p>No challenges found matching your criteria.</p>
              <button
                className={styles.reset_button}
                onClick={() => {
                  setSelectedChips(["all"]);
                  setSearchQuery("");
                }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChallengeList;
